// cryptoE2EE.js
//
// Browser-side primitives for the end-to-end encrypted channels feature.
// All operations use the Web Crypto API; no third-party crypto dependency.
//
// Wire format for an encrypted message body:
//   "enc:v1:<base64-iv>:<base64-ciphertext-with-gcm-tag>"
// Both segments are url-safe base64 (no '+' or '/') so they survive the
// realtime/JSON path without re-encoding. The 12-byte IV is random per
// message; AES-256-GCM appends a 16-byte tag we don't separate out — the
// Web Crypto API handles it transparently.
//
// Key handling rule: the *derived AES key* lives only in JS memory. We
// never persist it to localStorage / IndexedDB / sessionStorage. A user
// who closes the tab is back to "locked" and must enter the passphrase
// again — this is the deliberate trade against a stolen device giving
// up the encrypted archive forever.

// ─── base64 helpers ───────────────────────────────────────────────────
function b64uEncode(buf) {
  const u = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (const b of u) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64uDecode(s) {
  const pad = (4 - (s.length % 4)) % 4;
  const t = (s + '='.repeat(pad)).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(t);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const ENC = new TextEncoder();
const DEC = new TextDecoder();

// Known plaintext used to verify a derived key is correct without
// revealing the passphrase. Stored in firm_encryption.verification_token
// in encrypted form.
const VERIFY_PLAINTEXT = 'docketflow-encryption-verify-v1';

// Default PBKDF2 iteration count. Tunable per-firm (stored on the
// firm_encryption row) so we can ratchet up over time as hardware
// improves without breaking existing setups.
export const DEFAULT_ITERATIONS = 200000;

export function generateSaltB64u(bytes = 16) {
  const buf = crypto.getRandomValues(new Uint8Array(bytes));
  return b64uEncode(buf);
}

/**
 * Derive an AES-256-GCM CryptoKey from a passphrase. Web Crypto:
 *   PBKDF2 SHA-256 → 256-bit key → import as AES-GCM
 * Returns a CryptoKey usable with encrypt/decrypt below.
 */
export async function deriveKey(passphrase, saltB64u, iterations = DEFAULT_ITERATIONS) {
  if (!passphrase || typeof passphrase !== 'string') throw new Error('passphrase required');
  if (!saltB64u) throw new Error('salt required');
  const baseKey = await crypto.subtle.importKey(
    'raw', ENC.encode(passphrase),
    'PBKDF2', false, ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: b64uDecode(saltB64u), iterations },
    baseKey,
    256,
  );
  return crypto.subtle.importKey('raw', bits, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/**
 * Encrypt a plaintext string to the wire format `enc:v1:<iv>:<ct>`.
 */
export async function encryptString(plaintext, key) {
  if (key == null) throw new Error('encryption key not unlocked');
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ctBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    ENC.encode(plaintext ?? ''),
  );
  return `enc:v1:${b64uEncode(iv)}:${b64uEncode(ctBuf)}`;
}

/**
 * Decrypt a wire-format envelope. Returns the plaintext, or throws on any
 * failure (bad key, tampered ciphertext, malformed envelope).
 */
export async function decryptString(envelope, key) {
  if (key == null) throw new Error('encryption key not unlocked');
  if (!isEncryptedEnvelope(envelope)) throw new Error('not an encrypted envelope');
  const parts = envelope.split(':');
  if (parts.length !== 4) throw new Error('envelope malformed');
  const [, , ivB64, ctB64] = parts;
  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: b64uDecode(ivB64) },
    key,
    b64uDecode(ctB64),
  );
  return DEC.decode(plainBuf);
}

/** True iff the string is in the `enc:v1:…` wire format. */
export function isEncryptedEnvelope(s) {
  return typeof s === 'string' && s.startsWith('enc:v1:');
}

/**
 * Create a verification token from a freshly-derived key. Stored on the
 * firm row so other users can later verify they typed the right
 * passphrase before the app trusts their key.
 */
export async function buildVerificationToken(key) {
  return encryptString(VERIFY_PLAINTEXT, key);
}

/**
 * Verify a derived key against the firm's stored verification token.
 * Returns true on a match; false on any kind of failure (wrong passphrase,
 * malformed token, etc.).
 */
export async function verifyKey(key, verificationTokenEnvelope) {
  if (!isEncryptedEnvelope(verificationTokenEnvelope)) return false;
  try {
    const plain = await decryptString(verificationTokenEnvelope, key);
    return plain === VERIFY_PLAINTEXT;
  } catch (_err) {
    return false;
  }
}
